import React, { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form/dist/react-hook-form.ie11';
import { InputMaskDate } from '../../../Helpers/Formatters';
import { CheckboxInput, TextInput, SelectInput } from '../../../Helpers/Inputs';
import BackNext from '../../Common/SubComponents/BackNext';
import GenderButtons from '../../Common/SubComponents/GenderButtons';
import { object, string, date, customValidators } from '../../../Helpers/Validators/customValidators';
import { getBasicInfo, setBasicInfo } from '../../../../sessionStorage';
import { sub } from 'date-fns';
import GetStateListForAflac from '../Helpers/StateListForAflac';
import { DELAWARE, NEWHAMPSHIRE, RHODE_ISLAND, VIRGINIA, WASHINGTON, NEW_JERSEY, NEW_YORK, GUAM, OHIO, PUERTO_RICO } from "../../../Helpers/Constants/States";
import Tooltip from '@material-ui/core/Tooltip';
import { withStyles } from '@material-ui/core/styles';
import InfoIcon from '@material-ui/icons/Info';
import { emptyStringToUndefined } from '../../../Helpers/Formatters/EmptyStringToUndefined.js';

export default function BasicInfo(props) {
    const states = GetStateListForAflac();
    const basicInfoInEligibleState = [DELAWARE, NEWHAMPSHIRE, RHODE_ISLAND, WASHINGTON, VIRGINIA, NEW_JERSEY, PUERTO_RICO, NEW_YORK, GUAM, OHIO];
    const { register, errors, handleSubmit, setValue } = useForm({
        validationSchema: BasicInfo.ValidationSchema(props.ageSpecs, basicInfoInEligibleState),
        mode: 'onBlur',
        reValidateMode: 'onBlur',
    });
    const handleValueChange = (field, value) => {
        setValue(field, value);
        props.onFieldChange(field, value);
    };
    const [error, setError] = useState(false);
    useEffect(() => {
        // manually register fields for validation that don't play nice with react-hook-form register
        register({ name: 'gender' });
        setValue('gender', props.formState.gender);
        register({ name: 'birthdate' });
        setValue('birthdate', props.formState.birthdate);
        register({ name: 'state' });
        setValue('state', props.formState.state);
        register({ name: 'hsaAccount' });
        setValue('hsaAccount', props.formState.hsaAccount);

    }, []);

    const onSubmit = () => {
        const basicInfoFromSessionStorage = getBasicInfo();
        basicInfoFromSessionStorage.gender = props.formState.gender;
        basicInfoFromSessionStorage.birthdate = props.formState.birthdate;
        basicInfoFromSessionStorage.state = props.formState.state;
        basicInfoFromSessionStorage.hsaAccount = props.formState.hsaAccount;
        setBasicInfo(basicInfoFromSessionStorage);
        props.onNext();
    };
    const onGetQuote = (props.formState.gender === '' || props.formState.birthdate === '' || props.formState.state === '');

    return (
        <div className="basic-info d-flex justify-content-center">
            <Form onSubmit={handleSubmit(onSubmit)} className="d-flex flex-column mt-4">
                <BasicInfoForm formState={props.formState} onGetQuote={onGetQuote} handleValueChange={handleValueChange} onFieldChange={props.onFieldChange} hideBackNext={props.hideBackNext} logoBottom={props.logoBottom} errors={errors} register={register} states={states} error={error} />
            </Form>
        </div>
    );
}

const LightTooltip = withStyles((theme) => ({
    tooltip: {
        backgroundColor: theme.palette.common.white,
        color: 'rgba(0, 0, 0, 0.87)',
        fontSize: 14,
        boxShadow: theme.shadows[1],
    },
}))(Tooltip);

const label = (<span className="light-tooltip">I have a Health Savings Account
    <LightTooltip title='The easiest way to find out if you have a Health Savings Account (HSA) is to ask your Human Resources (HR) Department. You can also check your W-2 to see if you have the code "W" in box 12.' placement="top" enterTouchDelay={0} ><InfoIcon className="ml-2" /></LightTooltip>
</span>);

function BasicInfoForm(props) {
    return (
        <div className="d-flex flex-column">
            <div className="text-danger text-center mb-3">{props.error}</div>
            <div className={`d-flex justify-content-center`}>
                <img src="/product-landing/supp/aflac-logo.png" alt="Aflac Logo" style={{ height: '73px', width: '160px' }} className="mb-2" />
            </div>

            <Form.Group className="m-0">
                <GenderButtons
                    selectedGender={props.formState.gender}
                    onClickMale={() => props.handleValueChange('gender', 'M')}
                    onClickFemale={() => props.handleValueChange('gender', 'F')}
                    error={props.errors.gender} />
            </Form.Group>

            <Form.Group >
                <TextInput
                    name="birthdate"
                    id="birthdate"
                    label="Date of Birth"
                    value={props.formState.birthdate}
                    errors={props.errors && props.errors.birthdate}
                    inputRef={props.register}
                    onChange={(e) => props.handleValueChange('birthdate', e.target.value)}
                    InputProps={{
                        inputComponent: InputMaskDate,
                    }}
                />
            </Form.Group>

            <Form.Group >
                <SelectInput
                    name="state"
                    id="state"
                    value={props.formState.state}
                    onChange={(e) => { props.handleValueChange('state', e.target.value); }}
                    label="State"
                    selectOptions={props.states}
                    errors={props.errors && props.errors.state}
                    inputRef={props.register}
                />
            </Form.Group>
            <Form.Group className={`m-0 `}>
                <CheckboxInput
                    name="hsaAccount"
                    id="hsaAccount"
                    checked={props.formState.hsaAccount}
                    onChange={(e) => props.onFieldChange('hsaAccount', e.target.checked)}
                    errors={props.errors && props.errors.hsaAccount}
                    inputRef={props.register}
                    label={label}
                    color="primary"
                />
            </Form.Group>

            <BackNext
                disabled={props.onGetQuote}
                hideBack={true}
                onBack={props.onBack}
                continueLabel={{ text: "Get a Quote" }}
                className="w-100"
            />

        </div>
    );

}

BasicInfo.propTypes = {
    formState: PropTypes.object.isRequired,
    onNext: PropTypes.func,
    onFieldChange: PropTypes.func.isRequired,
    onBack: PropTypes.func,
};

BasicInfo.ValidationSchema = (ageSpecs, basicInfoInEligibleState) => {
    return object().shape({
        gender: string().required('Gender is required.'),
        birthdate: date().transform(emptyStringToUndefined)
            .typeError('Invalid date.')
            .max(sub(new Date(), { years: ageSpecs.min, days: ageSpecs.minAgeDays || 0 }), `Product not available due to age restrictions.`)
            .min(sub(new Date(), { years: ageSpecs.max }), `Product not available due to age restrictions.`)
            .required('Birthdate is required.'),
        state: customValidators.string().isValidState('State is required.', basicInfoInEligibleState),

    });
};

BasicInfo.DefaultFormState = (prefill = false, existingBasicInfo = null) => {
    return {
        gender: prefill ? 'M' : (existingBasicInfo && existingBasicInfo.gender) || '',
        birthdate: prefill ? '11/15/1970' : (existingBasicInfo && existingBasicInfo.birthdate) || '',
        state: prefill ? 'ID' : (existingBasicInfo && existingBasicInfo.state) || '',
        hsaAccount: prefill ? true : (existingBasicInfo && existingBasicInfo.hsaAccount) || false,
    };
};



// WEBPACK FOOTER //
// ./src/components/Quoters/Aflac/steps/BasicInfo.js